import { useCallback, useEffect, useState } from 'react';
import { teamMembershipSchema, teamSchema, type LiveTopic, type Team } from '@forecastify/shared';
import type { FilterOption, ListFilter } from '@forecastify/ui';
import type { Translate } from '../i18n';
import { TEAM_TOPICS } from '../live/topics';

const MEMBER_TEAM_TOPICS: readonly LiveTopic[] = ['team', 'member'];
import { useLiveInvalidation } from '../live/use-live';
import { useSession } from '../session';
import { parseList } from './permissions-hook';

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ R-60 · the workspace's teams, for every filter and picker that offers one. │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * ⚠️ **One read, one shape, everywhere** — the people list, the person page, the dashboards, the
 * worklog and task lists, the request queues and the report builder all offer the same list, and a
 * screen deriving its own (as the old `/people/teams` did, from whoever held a name) would offer a
 * different set from the one beside it.
 *
 * ⚠️ **Live**: a team created or renamed on Settings → Teams reaches an open filter without a reload.
 * A failed read leaves the list empty, which hides the filter rather than breaking the screen.
 */
export function useTeams(): readonly Team[] {
  const session = useSession();
  const [teams, setTeams] = useState<readonly Team[]>([]);

  const reload = useCallback(async (): Promise<void> => {
    if (session.workspaceId === null) return;
    const response = await session.request('/teams').catch(() => null);
    if (response?.ok !== true) return;
    setTeams(parseList(teamSchema, await response.json()) ?? []);
    /* eslint-disable-next-line react-hooks/exhaustive-deps -- `session` is a fresh object every
       render; depending on it refetches for ever. */
  }, [session.workspaceId]);

  useEffect(() => {
    void reload();
  }, [reload]);
  useLiveInvalidation(TEAM_TOPICS, reload);

  return teams;
}

/**
 * The toolbar filter, the same on every screen: *Csapat*, defaulting to every team.
 *
 * ⚠️ **Absent while the workspace has no teams** — returned as a spreadable list — because a filter
 * whose only option is *all* is a control that does nothing.
 */
export function teamFilter(
  t: Translate,
  teams: readonly { readonly id: string; readonly name: string }[],
  value: string,
  onChange: (value: string) => void,
): ListFilter[] {
  if (teams.length === 0) return [];
  return [
    {
      key: 'team',
      label: t('filter.team'),
      value,
      onChange,
      options: [
        { value: '', label: t('filter.allTeams') },
        ...teams.map((team) => ({ value: team.id, label: team.name })),
      ],
    },
  ];
}

/**
 * Which teams each person is in, by member id — for the lists that filter on the client (the
 * settings grids, a project's people).
 *
 * ⚠️ **`/teams/members`, readable by everybody the filter is shown to**, and unbounded by the people
 * list's 200 ceiling.
 */
export function useMemberTeams(): ReadonlyMap<string, readonly string[]> {
  const session = useSession();
  const [map, setMap] = useState<ReadonlyMap<string, readonly string[]>>(new Map());

  const reload = useCallback(async (): Promise<void> => {
    if (session.workspaceId === null) return;
    /* ⚠️ Not `/people`, which a member without `people.view` cannot read — the filter must work for
       everybody who can see it (27 Oct 2026). */
    const response = await session.request('/teams/members').catch(() => null);
    if (response?.ok !== true) return;
    const rows = parseList(teamMembershipSchema, await response.json());
    if (rows === null) return;
    setMap(new Map(rows.map((row) => [row.memberId, row.teamIds])));
    /* eslint-disable-next-line react-hooks/exhaustive-deps -- `session` is a fresh object every
       render; depending on it refetches for ever. */
  }, [session.workspaceId]);

  useEffect(() => {
    void reload();
  }, [reload]);
  /* ⚠️ `member` too: putting somebody in a team is an edit of the person. */
  useLiveInvalidation(MEMBER_TEAM_TOPICS, reload);

  return map;
}

/** Whether a person passes the team filter — `''` passes everybody. */
export function inTeam(
  memberTeams: ReadonlyMap<string, readonly string[]>,
  memberId: string,
  teamId: string,
): boolean {
  return teamId === '' || (memberTeams.get(memberId) ?? []).includes(teamId);
}

/** The settings pages' filter control — `SettingsPage`'s own shape. Absent with no teams. */
export function settingsTeamFilter(
  t: Translate,
  teams: readonly { readonly id: string; readonly name: string }[],
  value: string,
  onChange: (value: string) => void,
): {
  key: string;
  value: string;
  ariaLabel: string;
  options: FilterOption[];
  onChange: (value: string) => void;
}[] {
  if (teams.length === 0) return [];
  return [
    {
      key: 'team',
      value,
      ariaLabel: t('filter.team'),
      options: [
        { value: '', label: t('filter.allTeams') },
        ...teams.map((team) => ({ value: team.id, label: team.name })),
      ],
      onChange,
    },
  ];
}
